import React, { memo, useCallback, useEffect } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { ChipButton } from '@/components/ChipButton';
import { SizedBox as SizeBoxComponent } from '@/components/SizedBox';
import { common } from '@/styles/styles';
import { PositionFilters } from '@/types/api.generated';

import {
    usePlayerFiltersActions,
    usePlayerFiltersScrollControls,
    usePlayerFiltersState,
} from '../hooks/use-player-filters';

const SizedBox = () => <SizeBoxComponent value={8} />;

export const PlayerPositionsFilters = memo(({ positionFilters }: { positionFilters?: PositionFilters[] }) => {
    const { playerPositionFilter } = usePlayerFiltersState();
    const { setPlayerPositionFilter } = usePlayerFiltersActions();
    const { scrollToFilters } = usePlayerFiltersScrollControls();

    const firstPositionFilter = positionFilters?.[0];

    useEffect(() => {
        // Set the filter on the first item if it exists and has not been set initially
        if (playerPositionFilter === undefined && firstPositionFilter) {
            setPlayerPositionFilter(firstPositionFilter.position ?? '', firstPositionFilter.positionDescription ?? '');
        }
    }, [firstPositionFilter, playerPositionFilter, setPlayerPositionFilter]);

    const onSelect = useCallback(
        (position: string, positionDescription: string) => {
            scrollToFilters();
            setPlayerPositionFilter(position, positionDescription);
        },
        [scrollToFilters, setPlayerPositionFilter]
    );

    const renderItem = useCallback(
        ({ item }: { item: NonNullable<typeof positionFilters>[number] }) => (
            <PositionFilterChip
                position={item.position ?? ''}
                label={item.label ?? ''}
                positionDescription={item.positionDescription ?? ''}
                active={playerPositionFilter === item.position}
                onSelect={onSelect}
            />
        ),
        [playerPositionFilter, onSelect]
    );

    if (!positionFilters?.length) {
        return null;
    }

    /*
    Nesting the FlatList inside a ScrollView to allow horizontal scrolling - the "spring" effect, even when the list of item is short and doesn't require scrolling
    If they have different scroll directions, there will be no warning.
    Also it fixed the swipe gesture in the Chip bar that allows the user to navigate between tabs
    */
    return (
        <ScrollView style={common.flex} scrollEnabled={false}>
            <FlatList
                horizontal
                data={positionFilters}
                ListFooterComponent={<SizeBoxComponent value={16} />}
                ListHeaderComponent={<SizeBoxComponent value={16} />}
                ItemSeparatorComponent={SizedBox}
                contentContainerStyle={styles.flatListStyle}
                showsHorizontalScrollIndicator={false}
                renderItem={renderItem}
            />
        </ScrollView>
    );
});

PlayerPositionsFilters.displayName = 'PlayerPositionsFilters';

const PositionFilterChip = memo(
    ({
        position,
        label,
        positionDescription,
        active,
        onSelect,
    }: {
        position: string;
        label: string;
        positionDescription: string;
        active: boolean;
        onSelect: (position: string, positionDescription: string) => void;
    }) => {
        const handlePress = useCallback(() => {
            onSelect(position, positionDescription);
        }, [onSelect, position, positionDescription]);

        return <ChipButton label={label} active={active} onPress={handlePress} testID="chipButton" />;
    }
);

const styles = StyleSheet.create({
    flatListStyle: {
        paddingVertical: 16,
        minWidth: '100%',
    },
});
